import React, { useState } from 'react';

interface SpecialCollection {
  id: string;
  title: string;
  description: string;
  coverImage: string;
  itemCount: number;
  category: string;
  highlightItems?: CollectionItem[];
}

interface CollectionItem {
  id: string;
  title: string;
  description: string;
  image?: string;
  year?: string;
  type: string;
}

interface SpecialCollectionsProps {
  collections: SpecialCollection[];
  onCollectionClick: (collectionId: string) => void;
  onItemClick: (collectionId: string, itemId: string) => void;
}

const SpecialCollections: React.FC<SpecialCollectionsProps> = ({
  collections,
  onCollectionClick,
  onItemClick
}) => {
  const [expandedCollection, setExpandedCollection] = useState<string | null>(null);

  const toggleCollection = (id: string) => {
    if (expandedCollection === id) {
      setExpandedCollection(null);
    } else {
      setExpandedCollection(id);
      onCollectionClick(id);
    }
  };

  // Grupăm colecțiile pe categorii
  const collectionsByCategory: Record<string, SpecialCollection[]> = {};
  collections.forEach(collection => {
    if (!collectionsByCategory[collection.category]) {
      collectionsByCategory[collection.category] = [];
    }
    collectionsByCategory[collection.category].push(collection);
  });

  return (
    <div className="space-y-16">
      {/* Descriere colecții speciale */}
      <div className="bg-gray-50 p-8 rounded-xl text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Colecții Speciale și Arhive</h1>
        <p className="text-gray-600 max-w-3xl mx-auto">
          Explorați colecțiile noastre speciale și arhivele digitale care includ materiale rare, 
          manuscrise istorice, documente de valoare culturală și patrimonială. Aceste colecții oferă o 
          perspectivă unică asupra istoriei, literaturii și culturii.
        </p>
      </div>

      {/* Listare categorii și colecții */}
      {Object.entries(collectionsByCategory).map(([category, categoryCollections]) => (
        <div key={category} className="space-y-8">
          <h2 className="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-2">
            {category}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categoryCollections.map(collection => (
              <div key={collection.id} className="space-y-4">
                {/* Card colecție */}
                <div 
                  className="bg-white rounded-xl overflow-hidden shadow-md transition-transform hover:shadow-lg cursor-pointer transform hover:-translate-y-1"
                  onClick={() => toggleCollection(collection.id)}
                >
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={collection.coverImage} 
                      alt={collection.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                      <div className="p-4 text-white">
                        <h3 className="text-xl font-bold">{collection.title}</h3>
                        <p className="text-sm text-gray-100">{collection.itemCount} elemente</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-600 text-sm line-clamp-3">{collection.description}</p>
                    <div className="flex justify-end mt-4">
                      <button 
                        className="text-blue-600 flex items-center text-sm font-medium hover:text-blue-800"
                      >
                        {expandedCollection === collection.id ? 'Ascunde detalii' : 'Arată detalii'}
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className={`h-4 w-4 ml-1 transition-transform transform ${expandedCollection === collection.id ? 'rotate-180' : ''}`} 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Detalii colecție expandată */}
                {expandedCollection === collection.id && collection.highlightItems && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 animate-fadeIn">
                    <h4 className="font-bold text-gray-700 mb-4">Elemente reprezentative</h4>
                    <div className="space-y-4">
                      {collection.highlightItems.map(item => (
                        <div 
                          key={item.id}
                          className="flex bg-white p-3 rounded-lg items-center space-x-4 cursor-pointer hover:bg-blue-50 transition"
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemClick(collection.id, item.id);
                          }}
                        >
                          {item.image && (
                            <div className="w-16 h-16 flex-shrink-0">
                              <img 
                                src={item.image} 
                                alt={item.title}
                                className="w-full h-full object-cover rounded"
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h5 className="font-medium text-gray-800 truncate">{item.title}</h5>
                            <p className="text-sm text-gray-500 truncate">{item.type}{item.year ? `, ${item.year}` : ''}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{item.description}</p>
                          </div>
                          <div className="text-blue-600">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-center">
                      <button 
                        className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCollectionClick(collection.id);
                        }}
                      >
                        Explorează colecția completă
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Secțiune resurse adiționale */}
      <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 flex flex-col md:flex-row items-center">
        <div className="mb-4 md:mb-0 md:mr-6 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-blue-800 mb-2">Accesul la colecțiile speciale și arhive</h3>
          <p className="text-blue-700 mb-4">
            Colecțiile noastre speciale și arhivele sunt digitizate continuu și disponibile pentru cercetare și 
            studiu. Anumite materiale pot necesita drepturi de acces speciale pentru consultare completă.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#" className="bg-white text-blue-700 hover:bg-blue-100 transition px-4 py-2 rounded-lg shadow-sm inline-flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ghid de utilizare
            </a>
            <a href="#" className="bg-white text-blue-700 hover:bg-blue-100 transition px-4 py-2 rounded-lg shadow-sm inline-flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              Solicită asistență
            </a>
            <a href="#" className="bg-white text-blue-700 hover:bg-blue-100 transition px-4 py-2 rounded-lg shadow-sm inline-flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
              Solicită acces special
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialCollections; 